import React, { useState, useEffect } from 'react';
import { getApi } from '../../services/api';

/* ─── colour palette per rank index ─── */
const RANK_COLORS = [
  '#f59e0b', // Super     – amber
  '#6366f1', // Director  – indigo
  '#a8a9ad', // Silver    – silver
  '#f7c948', // Gold      – gold
  '#e11d48', // Ruby      – ruby-red
  '#a78bfa', // Platinum  – violet
  '#00d4ff', // Gold VII  – cyan
];

/* ─── skeleton card ─── */
const SkeletonCard = () => (
  <div className="col-lg-4 col-md-6">
    <div className="p-card" style={{ opacity: 0.5 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{ width: 52, height: 52, borderRadius: 12, background: 'rgba(255,255,255,0.08)' }} />
        <div>
          <div style={{ width: 90, height: 14, borderRadius: 6, background: 'rgba(255,255,255,0.1)', marginBottom: 6 }} />
          <div style={{ width: 60, height: 10, borderRadius: 6, background: 'rgba(255,255,255,0.06)' }} />
        </div>
      </div>
      {[1, 2, 3].map(i => (
        <div key={i} style={{ width: '100%', height: 10, borderRadius: 6, background: 'rgba(255,255,255,0.06)', marginBottom: 8 }} />
      ))}
    </div>
  </div>
);

/* ─── format number with commas ─── */
const fmt = (n) => Number(n).toLocaleString();

const Rank = () => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const fetchRanks = () => {
    setLoading(true);
    setError(null);
    getApi('user-rank')
      .then(res => {
        if (res.data?.status) {
          setData(res.data);
        } else {
          setError(res.data?.message || 'Failed to load rank data.');
        }
      })
      .catch(err => {
        setError(err?.response?.data?.message || 'Network error. Please try again.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchRanks(); }, []);

  /* ─── derived values ─── */
  const ranks        = data?.ranks        || [];
  const strongLeg    = data?.strong_leg   ?? 0;
  const weakerLeg    = data?.weaker_leg   ?? 0;

  const achievedRanks = ranks.filter(r => r.rankStatus);
  const currentRank   = achievedRanks[achievedRanks.length - 1] || null;
  const nextRank      = ranks.find(r => !r.rankStatus)           || null;

  /* progress toward next rank: weaker leg vs required right_business */
  const nextProgress = nextRank
    ? Math.min(100, Math.round((weakerLeg / nextRank.right_business) * 100))
    : 100;

  /* ─── Hero stats ─── */
  const heroStats = [
    { label: 'Strong Leg',     value: `$${fmt(strongLeg)}`,  color: 'var(--page-accent)' },
    { label: 'Weaker Leg',     value: `$${fmt(weakerLeg)}`,  color: '#f59e0b' },
    { label: 'Next Rank',      value: nextRank?.rank_name || '—', color: nextRank ? RANK_COLORS[ranks.indexOf(nextRank)] : '#aaa' },
    { label: 'Ranks Achieved', value: `${achievedRanks.length} / ${ranks.length}`, color: 'var(--page-blue)' },
  ];

  return (
    <div className="page-wrapper">

      {/* ── Header ── */}
      <div className="page-header mb-4">
        <div className="section-label">Achievements</div>
        <h2 className="page-title">Rank &amp; Rewards</h2>
        <p className="page-subtitle">Track your progress and unlock exclusive benefits</p>
      </div>

      {/* ── Error Banner ── */}
      {error && !loading && (
        <div style={{
          background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 14, padding: '1rem 1.25rem', marginBottom: '1.5rem',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <i className="bi bi-exclamation-triangle-fill" style={{ color: '#ef4444', fontSize: '1.1rem' }} />
          <span style={{ color: '#fca5a5', fontSize: '0.9rem' }}>{error}</span>
          <button
            onClick={fetchRanks}
            style={{
              marginLeft: 'auto', background: 'rgba(239,68,68,0.2)',
              border: '1px solid rgba(239,68,68,0.4)', borderRadius: 8,
              padding: '0.3rem 0.75rem', color: '#fca5a5', fontSize: '0.82rem', cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* ── Current Rank Hero ── */}
      {loading ? (
        <div className="p-card mb-4" style={{ minHeight: 180 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 140 }}>
            <div style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              <div className="spinner-border spinner-border-sm me-2" role="status" style={{ color: 'var(--page-accent)' }} />
              Loading rank data…
            </div>
          </div>
        </div>
      ) : data && (
        <div className="p-card mb-4" style={{
          background: currentRank
            ? `linear-gradient(135deg, ${RANK_COLORS[ranks.indexOf(currentRank)]}0D, rgba(0,212,255,0.03))`
            : 'linear-gradient(135deg, rgba(0,255,149,0.06), rgba(0,212,255,0.03))',
          borderColor: currentRank
            ? `${RANK_COLORS[ranks.indexOf(currentRank)]}33`
            : 'rgba(0,255,149,0.2)',
        }}>
          <div className="row align-items-center g-4">

            {/* Rank Badge */}
            <div className="col-md-3 text-center">
              {currentRank ? (
                <>
                  <div style={{
                    width: 100, height: 100, margin: '0 auto', borderRadius: '50%',
                    background: `${RANK_COLORS[ranks.indexOf(currentRank)]}1A`,
                    border: `3px solid ${RANK_COLORS[ranks.indexOf(currentRank)]}66`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: `0 0 24px ${RANK_COLORS[ranks.indexOf(currentRank)]}33`,
                    overflow: 'hidden',
                  }}>
                    <img
                      src={currentRank.rank_image}
                      alt={currentRank.rank_name}
                      style={{ width: 72, height: 72, objectFit: 'contain' }}
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  </div>
                  <h3 style={{ marginTop: '0.75rem', fontWeight: 800, color: RANK_COLORS[ranks.indexOf(currentRank)], fontSize: '1.4rem' }}>
                    {currentRank.rank_name}
                  </h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', margin: 0 }}>Current Rank</p>
                </>
              ) : (
                <>
                  <div style={{
                    width: 100, height: 100, margin: '0 auto', borderRadius: '50%',
                    background: 'rgba(255,255,255,0.04)', border: '3px dashed rgba(255,255,255,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <i className="bi bi-trophy" style={{ fontSize: '2rem', color: 'rgba(255,255,255,0.2)' }} />
                  </div>
                  <h3 style={{ marginTop: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', fontSize: '1.2rem' }}>
                    No Rank Yet
                  </h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', margin: 0 }}>Start earning to rank up</p>
                </>
              )}
            </div>

            {/* Stats Grid */}
            <div className="col-md-9">
              <div className="row g-3 mb-3">
                {heroStats.map((s, i) => (
                  <div key={i} className="col-6 col-lg-3">
                    <div style={{
                      background: 'rgba(255,255,255,0.03)', borderRadius: 12,
                      padding: '0.75rem 1rem', border: '1px solid rgba(255,255,255,0.06)',
                    }}>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
                        {s.label}
                      </div>
                      <div style={{ fontSize: '1rem', fontWeight: 800, color: s.color, fontFamily: 'JetBrains Mono, monospace', marginTop: 2 }}>
                        {s.value}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Progress to Next Rank */}
              {nextRank ? (
                <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: 12, padding: '1rem' }}>
                  <div className="p-progress-header mb-2">
                    <span className="p-progress-label">
                      Progress to{' '}
                      <strong style={{ color: RANK_COLORS[ranks.indexOf(nextRank)] }}>
                        {nextRank.rank_name}
                      </strong>
                    </span>
                    <span className="p-progress-value">{nextProgress}%</span>
                  </div>
                  <div className="p-progress" style={{ height: 10 }}>
                    <div className="p-progress-bar" style={{
                      width: `${nextProgress}%`,
                      background: `linear-gradient(90deg, ${RANK_COLORS[ranks.indexOf(nextRank)]}, ${RANK_COLORS[ranks.indexOf(nextRank)]}aa)`,
                    }} />
                  </div>
                  <div className="d-flex justify-content-between mt-2">
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                      Weaker leg: ${fmt(weakerLeg)}
                    </span>
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                      Required: ${fmt(nextRank.right_business)}
                    </span>
                  </div>
                </div>
              ) : (
                <div style={{
                  background: 'rgba(0,255,149,0.06)', borderRadius: 12, padding: '1rem',
                  border: '1px solid rgba(0,255,149,0.2)', textAlign: 'center',
                }}>
                  <i className="bi bi-trophy-fill" style={{ color: 'var(--page-accent)', fontSize: '1.4rem', display: 'block', marginBottom: 4 }} />
                  <span style={{ color: 'var(--page-accent)', fontWeight: 700 }}>All ranks achieved! 🎉</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Rank Cards Grid ── */}
      <div className="row g-3 mb-4">
        {loading
          ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
          : ranks.map((rank, index) => {
              const color     = RANK_COLORS[index] || '#888';
              const achieved  = rank.rankStatus;
              const isCurrent = currentRank?.id === rank.id;
              const isNext    = nextRank?.id === rank.id;

              return (
                <div key={rank.id} className="col-lg-4 col-md-6">
                  <div className="p-card" style={{
                    borderColor: achieved ? `${color}44` : isNext ? `${color}22` : 'rgba(255,255,255,0.07)',
                    background: isCurrent
                      ? `linear-gradient(135deg, ${color}14, transparent)`
                      : achieved ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.01)',
                    opacity: achieved || isNext ? 1 : 0.7,
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden',
                  }}>

                    {/* Top accent line for current rank */}
                    {isCurrent && (
                      <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, height: 2,
                        background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
                      }} />
                    )}

                    {/* Card Header */}
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div className="d-flex align-items-center gap-2">
                        <div style={{
                          width: 52, height: 52, borderRadius: 12,
                          background: achieved ? `${color}1A` : 'rgba(255,255,255,0.04)',
                          border: `2px solid ${achieved ? `${color}44` : 'rgba(255,255,255,0.08)'}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          boxShadow: achieved ? `0 0 12px ${color}22` : 'none',
                          overflow: 'hidden',
                        }}>
                          <img
                            src={rank.rank_image}
                            alt={rank.rank_name}
                            style={{
                              width: 36, height: 36, objectFit: 'contain',
                              filter: achieved ? 'none' : 'grayscale(80%) opacity(0.4)',
                            }}
                            onError={e => { e.target.style.display = 'none'; }}
                          />
                        </div>
                        <div>
                          <h5 style={{ margin: 0, fontWeight: 800, fontSize: '1rem',
                            color: achieved ? color : 'rgba(255,255,255,0.45)' }}>
                            {rank.rank_name}
                          </h5>
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                            Reward:{' '}
                            <strong style={{ color: achieved ? '#f7c948' : 'rgba(255,255,255,0.3)' }}>
                              ${fmt(rank.reward)}
                            </strong>
                          </span>
                        </div>
                      </div>

                      {/* Status badge */}
                      {achieved ? (
                        <span className="s-pill success">
                          <i className="bi bi-check2" /> Achieved
                        </span>
                      ) : isNext ? (
                        <span className="s-pill pending">In Progress</span>
                      ) : (
                        <span style={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.15)' }}>
                          <i className="bi bi-lock" />
                        </span>
                      )}
                    </div>

                    {/* Rank Requirements */}
                    <div style={{
                      background: 'rgba(0,0,0,0.2)', borderRadius: 10, padding: '0.75rem',
                      marginBottom: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem',
                    }}>
                      {[
                        { label: 'Team Business',  value: `$${fmt(rank.team_business)}` },
                        { label: 'Left Business',  value: `$${fmt(rank.left_business)}` },
                        { label: 'Right Business', value: `$${fmt(rank.right_business)}` },
                        { label: 'Direct Refs',    value: rank.direct },
                      ].map((item, i) => (
                        <div key={i}>
                          <div style={{ fontSize: '0.65rem', color: 'var(--text-secondary)',
                            textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                            {item.label}
                          </div>
                          <div style={{ fontSize: '0.82rem', fontWeight: 700,
                            color: achieved ? color : 'rgba(255,255,255,0.35)',
                            fontFamily: 'JetBrains Mono, monospace' }}>
                            {item.value}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Progress bar only for next rank */}
                    {isNext && (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>Weaker leg progress</span>
                          <span style={{ fontSize: '0.72rem', color, fontWeight: 700 }}>{nextProgress}%</span>
                        </div>
                        <div className="p-progress">
                          <div className="p-progress-bar" style={{
                            width: `${nextProgress}%`,
                            background: `linear-gradient(90deg, ${color}, ${color}aa)`,
                          }} />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                          <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>${fmt(weakerLeg)}</span>
                          <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>${fmt(rank.right_business)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
        }
      </div>

      {/* ── Overview Summary ── */}
      {!loading && data && (
        <div className="p-card" style={{
          background: 'linear-gradient(135deg, rgba(0,212,255,0.05), rgba(168,85,247,0.05))',
          borderColor: 'rgba(0,212,255,0.2)',
        }}>
          <p className="section-label">Rank Overview</p>
          <div className="row g-3">
            {[
              { label: 'Total Ranks',  val: ranks.length,         icon: 'bi-trophy',            color: 'var(--page-accent)' },
              { label: 'Achieved',     val: achievedRanks.length, icon: 'bi-check-circle',       color: '#22c55e' },
              { label: 'Strong Leg',   val: `$${fmt(strongLeg)}`, icon: 'bi-arrow-up-right',     color: 'var(--page-blue)' },
              { label: 'Weaker Leg',   val: `$${fmt(weakerLeg)}`, icon: 'bi-arrow-down-right',   color: '#f59e0b' },
            ].map((b, i) => (
              <div key={i} className="col-md-3 col-6 text-center">
                <i className={`bi ${b.icon}`} style={{ fontSize: '1.5rem', color: b.color, marginBottom: 6, display: 'block' }} />
                <div style={{ fontSize: '1.2rem', fontWeight: 800, color: 'white', fontFamily: 'JetBrains Mono, monospace' }}>
                  {b.val}
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{b.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
};

export default Rank;